import type React from "react";

import { Box, Stack, Typography } from "@mui/material";

import type { FilterSection } from "shared/atoms/FilterDrawer";
import { MUISelect } from "shared/atoms/MUI";
import { renderChipSelectValue } from "utils/ui/renderChipSelectValue";

import type { TherapistAssignmentRole } from "../queries/TherapistAssignmentRole";

import { Input } from "./helpers";
import type { Filter } from "./useFilters";

const createSection = (title: string, component: React.ReactNode): FilterSection => ({
  title,
  component: (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 14 }}>
        {title}
      </Typography>
      {component}
    </Box>
  ),
});

interface CreateFilterSectionsProps {
  t: (key: string) => string;
  tasks: Filter[];
  taskOptions: { label: string; value: Filter }[];
  getSelected: (filterArray: (Filter | TherapistAssignmentRole)[]) => (Filter | TherapistAssignmentRole)[];
  handleMultiSelectChange: (
    filterArray: (Filter | TherapistAssignmentRole)[],
    event: { target: { value: unknown } }
  ) => void;
  statusCheckboxes: React.ReactElement[];
  fromInput: string;
  toInput: string;
  handleWeekInput: (isFrom: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  ailmentOptions: { label: string; value: string }[];
  primaryJointSelection: string[];
  setFilter: (key: Filter, state: { active: boolean; selection: string[] }) => void;
  categories: Filter[];
  createOptions: (filters: Filter[], prefix?: string) => { label: string; value: Filter }[];
  membershipStatuses: Filter[];
  assignmentRoles: TherapistAssignmentRole[];
  assignmentRolesPhrases: Record<TherapistAssignmentRole, string>;
}

export const createFilterSections = ({
  t,
  tasks,
  taskOptions,
  getSelected,
  handleMultiSelectChange,
  statusCheckboxes,
  fromInput,
  toInput,
  handleWeekInput,
  ailmentOptions,
  primaryJointSelection,
  setFilter,
  categories,
  createOptions,
  membershipStatuses,
  assignmentRoles,
  assignmentRolesPhrases,
}: CreateFilterSectionsProps): FilterSection[] => {
  return [
    createSection(
      t(`patients.tasks`),
      <MUISelect
        multiple
        value={getSelected(tasks)}
        options={taskOptions}
        onChange={e => handleMultiSelectChange(tasks, e)}
        renderValue={selected =>
          renderChipSelectValue({
            selected,
            translateFn: (s: Filter) => t(`patients.${s}` as never),
            emptyMessage: t("diagnosis.categories.ALL"),
          })
        }
        size="small"
        displayEmpty
        sx={{ width: "100%" }}
      />
    ),
    {
      title: t(`patients.status`),
      component: (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 14 }}>
            {t(`patients.status`)}
          </Typography>
          <Stack direction="row" spacing={2}>
            {statusCheckboxes}
          </Stack>
        </Box>
      ),
    },
    {
      title: t(`patients.week`),
      component: (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 14 }}>
            {t(`patients.week`)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>{t("patients.week")}</span>
            <Input
              type="number"
              value={fromInput}
              onChange={handleWeekInput(true)}
              min={0}
              style={{ flex: 1, minWidth: 60 }}
            />
            <span style={{ fontSize: 14 }}>{t("patients.to")}</span>
            <Input
              type="number"
              value={toInput}
              onChange={handleWeekInput(false)}
              min={0}
              style={{ flex: 1, minWidth: 60 }}
            />
          </Box>
        </Box>
      ),
    },
    createSection(
      t(`patients.ailment`),
      <MUISelect
        multiple
        value={primaryJointSelection}
        options={ailmentOptions}
        onChange={e =>
          setFilter("primary_joint", {
            active: (e.target.value as string[]).length > 0,
            selection: e.target.value as string[],
          })
        }
        renderValue={selected =>
          renderChipSelectValue({
            selected,
            translateFn: (s: string) => ailmentOptions.find(opt => opt.value === s)?.label || s,
            emptyMessage: t("diagnosis.categories.ALL"),
          })
        }
        size="small"
        displayEmpty
        sx={{ width: "100%" }}
      />
    ),
    createSection(
      t(`patients.category`),
      <MUISelect
        multiple
        value={getSelected(categories)}
        options={createOptions(categories)}
        onChange={e => handleMultiSelectChange(categories, e)}
        renderValue={selected =>
          renderChipSelectValue({
            selected,
            translateFn: (s: Filter) => t(`patients.${s}` as never),
            emptyMessage: t("diagnosis.categories.ALL"),
          })
        }
        size="small"
        displayEmpty
        sx={{ width: "100%" }}
      />
    ),
    createSection(
      t(`patients.membershipStatus`),
      <MUISelect
        multiple
        value={getSelected(membershipStatuses)}
        options={createOptions(membershipStatuses)}
        onChange={e => handleMultiSelectChange(membershipStatuses, e)}
        renderValue={selected =>
          renderChipSelectValue({
            selected,
            translateFn: (s: Filter) => t(`patients.${s}` as never),
            emptyMessage: t("diagnosis.categories.ALL"),
          })
        }
        size="small"
        displayEmpty
        sx={{ width: "100%" }}
      />
    ),
    createSection(
      t(`patients.assignmentRole`),
      <MUISelect
        multiple
        value={getSelected(assignmentRoles)}
        options={assignmentRoles.map(role => ({ label: assignmentRolesPhrases[role], value: role }))}
        onChange={e => handleMultiSelectChange(assignmentRoles, e)}
        renderValue={selected =>
          renderChipSelectValue({
            selected,
            translateFn: (s: TherapistAssignmentRole) => assignmentRolesPhrases[s],
            emptyMessage: t("diagnosis.categories.ALL"),
          })
        }
        size="small"
        displayEmpty
        sx={{ width: "100%" }}
      />
    ),
  ];
};
